import {
  BriefcaseBusiness as Work,
  ChevronDown as ExpandMore,
  ChevronUp as ExpandLess,
  GraduationCap as School,
  Home,
  Plus as Add,
  Trash2 as Delete,
  X,
} from "lucide-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { editStudent, getUserByCompanyCode } from "../../../api/action/user";
import { InputField } from "../../../components/InputField";
import { Alert as UiAlert } from "../../../components/ui/Feedback";
import { ProfileHead } from "../ProfileHead";
import SecurityBox from "./SecurityBox";
import IndianDatePicker from "./IndianDatePicker";

const Grid = ({ children, className = "", container, item, spacing = 0, xs }) => {
  if (container) {
    return (
      <div className={`flex flex-wrap ${spacing ? "gap-y-4" : ""} ${className}`}>
        {children}
      </div>
    );
  }

  const widthClass = xs === 12 ? "w-full" : "w-full md:w-1/2";
  return <div className={`${item ? widthClass : ""} px-2 ${className}`}>{children}</div>;
};

const Collapse = ({ children, in: isOpen }) => (isOpen ? <div>{children}</div> : null);

const IconButton = ({ children, className = "", type = "button", ...props }) => (
  <button
    type={type}
    className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-slate-100 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Snackbar = ({ anchorOrigin = {}, autoHideDuration, children, onClose, open }) => {
  useEffect(() => {
    if (!open || !autoHideDuration) return undefined;
    const timer = window.setTimeout(() => onClose?.(), autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [autoHideDuration, onClose, open]);

  if (!open) return null;

  const verticalClass = anchorOrigin.vertical === "top" ? "top-5" : "bottom-5";
  const horizontalClass = anchorOrigin.horizontal === "center"
    ? "left-1/2 -translate-x-1/2"
    : anchorOrigin.horizontal === "right"
      ? "right-5"
      : "left-5";

  return <div className={`fixed ${verticalClass} ${horizontalClass} z-50 w-[calc(100%-2rem)] max-w-md`}>{children}</div>;
};

const Alert = ({ children, onClose, severity = "info" }) => (
  <div className="relative">
    <UiAlert tone={severity} className="pr-10">{children}</UiAlert>
    {onClose && (
      <button
        type="button"
        aria-label="Close"
        className="absolute right-2 top-2 rounded p-1 text-slate-500 transition hover:bg-white/70 hover:text-slate-900"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

const MyProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.auth?.data);
  const isAuthenticated = userData.isAuthenticated;
  const company = useSelector((state) => state.companyReducer.company);
  const companyPrimaryId = company?.primaryId;

  const userRequest = {
    primaryId: null,
    createdDate: null,
    createdBy: null,
    modifiedDate: null,
    modifiedBy: null,
    dynamicAttributes: null,
    deleted: false,
    personId: null,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    countryCode: null,
    profilePictureUrl: null,
    addressList: [
      {
        streetName1: "",
        streetName2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        type: null,
      },
    ],
    history: null,
    dob: "",
    status: "ACTIVE",
    description: null,
    password: "",
    salt: null,
    company: null,
    preferredCommunicationMethods: null,
    secrets: null,
    userId: null,
    userRole: "STUDENT",
    educationDetails: [
      {
        degree: "",
        educationLevel: "",
        schoolName: "",
        fieldOfStudy: "",
        startDate: "",
        endDate: "",
        currentlyStudying: "",
      },
    ],
    employmentDetails: [
      {
        jobRole: "",
        employerName: "",
        employmentType: "",
        startDate: "",
        endDate: "",
        currentlyEmployed: false,
      },
    ],
    documents: null,
    authProvider: null,
    allCourses: null,
    account: null,
    _id: "",
  };

  const [user, setUser] = useState(userRequest);
  const [openPersonalInfo, setOpenPersonalInfo] = useState(true);
  const [openAddressDetails, setOpenAddressDetails] = useState(true);
  const [openEducationDetails, setOpenEducationDetails] = useState(true);
  const [openEmploymentDetails, setOpenEmploymentDetails] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'

  const handleChange = (e, index, type) => {
    const { name, value } = e.target;

    if (name === "dob") {
      // Check if value is a valid date
      const dateParts = value.split('-');
      if (dateParts.length === 3) {
        const year = dateParts[0];

        if (year.length > 4) {
          // If the year has more than 4 digits, truncate it to the first 4 digits
          const validYear = year.slice(0, 4);

          // Rebuild the date with the corrected year
          const correctedDate = `${validYear}-${dateParts[1]}-${dateParts[2]}`;

          // Set the corrected date value
          setUser((prev) => ({
            ...prev,
            [name]: correctedDate,
          }));
          return; // Exit after updating the corrected date
        }
      }
    }

    if (name.startsWith("addressList")) {
      const [, fieldIndex, fieldName] = name.split(".");
      const newAddressList = [...user.addressList];
      newAddressList[fieldIndex][fieldName] = value;
      setUser((prev) => ({
        ...prev,
        addressList: newAddressList,
      }));
    } else if (type === "education") {
      const newEducationDetails = [...user.educationDetails];
      const [fieldName] = name.split(".").slice(-1);
      newEducationDetails[index][fieldName] =
        fieldName === "currentlyStudying"
          ? value === "true"
          : value;
      setUser((prev) => ({
        ...prev,
        educationDetails: newEducationDetails,
      }));
    } else if (type === "employment") {
      const newEmploymentDetails = [...user.employmentDetails];
      const [fieldName] = name.split(".").slice(-1);
      newEmploymentDetails[index][fieldName] = value;
      setUser((prev) => ({
        ...prev,
        employmentDetails: newEmploymentDetails,
      }));
    } else {
      setUser((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const addAddress = () => {
    setUser((prev) => ({
      ...prev,
      addressList: [
        ...prev.addressList,
        {
          streetName1: "",
          streetName2: "",
          city: "",
          state: "",
          zipcode: "",
          country: "",
          type: null,
        },
      ],
    }));
  };

  const removeAddress = (index) => {
    setUser((prev) => ({
      ...prev,
      addressList: prev.addressList.filter((_, i) => i !== index),
    }));
  };

  const addEducation = () => {
    setUser((prev) => ({
      ...prev,
      educationDetails: [
        ...prev.educationDetails,
        {
          degree: "",
          educationLevel: "",
          schoolName: "",
          fieldOfStudy: "",
          startDate: "",
          endDate: "",
          currentlyStudying: "",
        },
      ],
    }));
  };

  const removeEducation = (index) => {
    setUser((prev) => ({
      ...prev,
      educationDetails: prev.educationDetails.filter((_, i) => i !== index),
    }));
  };

  const addEmployment = () => {
    setUser((prev) => ({
      ...prev,
      employmentDetails: [
        ...prev.employmentDetails,
        {
          jobRole: "",
          employerName: "",
          employmentType: "",
          startDate: "",
          endDate: "",
          currentlyEmployed: false,
        },
      ],
    }));
  };

  const removeEmployment = (index) => {
    setUser((prev) => ({
      ...prev,
      employmentDetails: prev.employmentDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const profileData = {
        ...user,
        addressList: user.addressList.map((address) => ({
          ...address,
        })),
        educationDetails: user.educationDetails.map((education) => ({
          ...education,
        })),
        employmentDetails: user.employmentDetails.map((employment) => ({
          ...employment,
        })),
      };

      const response = await dispatch(editStudent(profileData));

      // Assuming the response indicates success
      if (response?.status) {
        setSnackbarMessage('Profile updated successfully!');
        setSnackbarSeverity('success');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      setSnackbarMessage(error?.message || 'Error updating profile. Please try again.');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const domainType = localStorage.getItem('domainType');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!companyPrimaryId) return;
        const response = await dispatch(getUserByCompanyCode(companyPrimaryId));
        const userData = Array.isArray(response) ? response[0] : response;

        if (userData) {
          setUser({
            ...userData,
            dob: userData.dob ? moment.utc(userData.dob).format('YYYY-MM-DD') : '',
            addressList: userData.addressList || [
              {
                streetName1: "",
                streetName2: "",
                city: "",
                state: "",
                zipcode: "",
                country: "",
                type: null,
              },
            ],
            educationDetails: userData.educationDetails || [
              {
                degree: "",
                educationLevel: "",
                schoolName: "",
                fieldOfStudy: "",
                startDate: "",
                endDate: "",
                currentlyStudying: "",
              },
            ],
            employmentDetails: userData.employmentDetails || [],
          });
        }
      } catch {
        setSnackbarMessage('Unable to load profile details. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchUserData();
  }, [companyPrimaryId, dispatch]);

  return (
    <>
      <ProfileHead activeItem="my profile" style={{ marginTop: '2px' }} />

      <div className="bg-white shadow-lg p-6">
        {/* Personal Info Section */}
        <div className="border-b-2 text-xl font-medium font-sans capitalize mb-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 text-center">
              <h2 style={{ fontWeight: "bold" }}>Personal Info</h2>
            </div>
            <IconButton
              onClick={() => setOpenPersonalInfo(!openPersonalInfo)}
              className="ml-2"
            >
              {openPersonalInfo ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </div>

          <Collapse in={openPersonalInfo}>
            {isAuthenticated ? (
              <form className="m-2 p-4" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <InputField
                    label={"First Name *"}
                    type="text"
                    name="firstName"
                    value={user.firstName}
                    handlechange={handleChange}
                  />
                  <InputField
                    label={"Last Name *"}
                    type="text"
                    name="lastName"
                    value={user.lastName}
                    handlechange={handleChange}
                  />
                  <InputField
                    label={"Email *"}
                    type="email"
                    name="email"
                    value={user.email}
                    handlechange={handleChange}
                  />
                  <InputField
                    label={"Phone *"}
                    type="text"
                    name="phoneNumber"
                    value={user.phoneNumber}
                    handlechange={handleChange}
                  />
                  <IndianDatePicker
                    label={"Date of Birth *"}
                    name="dob"
                    value={user.dob}
                    handleChange={handleChange}
                  />
                </Grid>
              </form>
            ) : (
              <h1>Values are not available</h1>
            )}
          </Collapse>
        </div>

        {/* Address Details Section */}
        {domainType !== 'INSTITUTION' && (
          <div className="border-b-2 text-xl font-medium font-sans capitalize mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-center">
                <h2 style={{ fontWeight: "bold" }}>Address Details</h2>
              </div>
              <IconButton
                onClick={() => setOpenAddressDetails(!openAddressDetails)}
                className="ml-2"
              >
                {openAddressDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </div>

            <Collapse in={openAddressDetails}>
              {user.addressList.map((address, index) => (
                <div key={index} className="mb-4 border p-4 rounded">
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      className="flex justify-between items-center"
                    >
                      <h3 className="text-lg font-bold text-blue-600 flex items-center">
                        <Home className="mr-2 text-blue-600" />
                        Address {index + 1}
                      </h3>
                      {index > 0 && (
                        <IconButton
                          type="button"
                          onClick={() => removeAddress(index)}
                          className="cursor-pointer text-red-600"
                        >
                          <Delete style={{ color: "red" }} />
                        </IconButton>
                      )}
                    </Grid>
                    <InputField
                      label={"Address Type *"}
                      type="select"
                      name={`addressList.${index}.type`}
                      value={address.type}
                      handlechange={(e) => handleChange(e, index)}
                      options={[
                        { label: "None" },
                        { value: "HOME", label: "Home" },
                        { value: "WORK", label: "Work" },
                        { value: "BILLING", label: "Billing" },
                        { value: "SHIPPING", label: "Shipping" },
                      ]}
                    />
                    <InputField
                      label={"Street 1 *"}
                      type="text"
                      name={`addressList.${index}.streetName1`}
                      value={address.streetName1}
                      handlechange={(e) => handleChange(e, index)}
                    />
                    <InputField
                      label={"Street 2 "}
                      type="text"
                      name={`addressList.${index}.streetName2`}
                      value={address.streetName2}
                      handlechange={(e) => handleChange(e, index)}
                    />
                    <InputField
                      label={"City *"}
                      type="text"
                      name={`addressList.${index}.city`}
                      value={address.city}
                      handlechange={(e) => handleChange(e, index)}
                    />
                    <InputField
                      label={"State *"}
                      type="text"
                      name={`addressList.${index}.state`}
                      value={address.state}
                      handlechange={(e) => handleChange(e, index)}
                    />
                    <InputField
                      label={"Country *"}
                      type="text"
                      name={`addressList.${index}.country`}
                      value={address.country}
                      handlechange={(e) => handleChange(e, index)}
                    />
                    <InputField
                      label={"Pincode/Zipcode *"}
                      type="text"
                      name={`addressList.${index}.zipcode`}
                      value={address.zipcode}
                      handlechange={(e) => handleChange(e, index)}
                    />
                  </Grid>
                </div>
              ))}
              <button
                type="button"
                onClick={addAddress}
                className="p-2 px-4 bg-blue-500 text-white rounded-md mt-6 flex items-center justify-center"
              >
                Add Address <Add className="ml-2" />
              </button>
              <br></br>
            </Collapse>
          </div>
        )}

        {/* Educational Details Section */}
        {domainType !== 'INSTITUTION' && (
          <div className="border-b-2 text-xl font-medium font-sans capitalize mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-center">
                <h2 style={{ fontWeight: "bold" }}>Educational Details</h2>
              </div>
              <IconButton
                onClick={() => setOpenEducationDetails(!openEducationDetails)}
                className="ml-2"
              >
                {openEducationDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </div>

            <Collapse in={openEducationDetails}>
              {user.educationDetails.map((education, index) => (
                <div key={index} className="mb-4 border p-4 rounded">
                  <h3
                    className="text-lg font-bold text-blue-600 flex items-center"
                    style={{ paddingBottom: "14px" }}
                  >
                    <School className="mr-2 text-blue-600" />
                    Education {index + 1}
                  </h3>
                  <Grid container spacing={3}>
                    <InputField
                      label={"Degree *"}
                      type="text"
                      name={`educationDetails.${index}.degree`}
                      value={education.degree}
                      handlechange={(e) => handleChange(e, index, "education")}
                    />
                    <InputField
                      label={"Field of study *"}
                      type="text"
                      name={`educationDetails.${index}.fieldOfStudy`}
                      value={education.fieldOfStudy}
                      handlechange={(e) => handleChange(e, index, "education")}
                    />
                    <InputField
                      label={"Institution *"}
                      type="text"
                      name={`educationDetails.${index}.schoolName`}
                      value={education.schoolName}
                      handlechange={(e) => handleChange(e, index, "education")}
                    />
                    <InputField
                      label={"Education Level *"}
                      type="select"
                      name={`educationDetails.${index}.educationLevel`}
                      value={education.educationLevel}
                      handlechange={(e) => handleChange(e, index, "education")}
                      options={[
                        { value: "None", label: "None" },
                        { value: "HIGHSCHOOL", label: "High School" },
                        { value: "COLLEGE_MASTERS", label: "College Masters" },
                        { value: "COLLEGE_DOCTORATE", label: "College Doctorate" },
                        { value: "COLLEGE_BACHELORS", label: "College Bachelors" },
                        { value: "TRADE_SCHOOL", label: "Trade School" },
                        { value: "COLLEGE_ASSOCIATES", label: "College Associates" },
                      ]}
                    />
                    <InputField
                      label={"Start Date *"}
                      type="date"
                      name={`educationDetails.${index}.startDate`}
                      value={education.startDate}
                      handlechange={(e) => handleChange(e, index, "education")}
                    />
                    <InputField
                      label={"End Date *"}
                      type="date"
                      name={`educationDetails.${index}.endDate`}
                      value={education.endDate}
                      handlechange={(e) => handleChange(e, index, "education")}
                    />
                    <InputField
                      label={"Currently Studying"}
                      type="select"
                      name={`educationDetails.${index}.currentlyStudying`}
                      value={education.currentlyStudying ? "true" : "false"}
                      handlechange={(e) => handleChange(e, index, "education")}
                      options={[
                        { value: "true", label: "Yes" },
                        { value: "false", label: "No" },
                      ]}
                    />
                  </Grid>
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      style={{ color: "red" }}
                      onClick={() => removeEducation(index)}
                    >
                      <Delete />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={addEducation}
                type="button"
                className="p-2 px-4 bg-blue-500 text-white rounded-md mt-6 flex items-center justify-center"
              >
                Add Education <Add className="ml-2" />
              </button>
              <br></br>
            </Collapse>
          </div>
        )}

        {/* Employment Details Section */}
        {domainType !== 'INSTITUTION' && (
          <div className="border-b-2 text-xl font-medium font-sans capitalize mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-center">
                <h2 style={{ fontWeight: "bold" }}>Employment Details</h2>
              </div>
              <IconButton
                onClick={() => setOpenEmploymentDetails(!openEmploymentDetails)}
                className="ml-2"
              >
                {openEmploymentDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </div>

            <Collapse in={openEmploymentDetails}>
              {user.employmentDetails.map((employment, index) => (
                <div key={index} className="mb-4 border p-4 rounded">
                  <h3
                    className="text-lg font-bold text-blue-600 flex items-center"
                    style={{ paddingBottom: "14px" }}
                  >
                    <Work className="mr-2 text-blue-600" />
                    Employment {index + 1}
                  </h3>
                  <Grid container spacing={3}>
                    <InputField
                      label={"Company Name *"}
                      type="text"
                      name={`employmentDetails.${index}.employerName`}
                      value={employment.employerName}
                      handlechange={(e) => handleChange(e, index, "employment")}
                    />
                    <InputField
                      label={"Job Title *"}
                      type="text"
                      name={`employmentDetails.${index}.jobRole`}
                      value={employment.jobRole}
                      handlechange={(e) => handleChange(e, index, "employment")}
                    />
                    <InputField
                      label={"Employment Type *"}
                      type="select"
                      name={`employmentDetails.${index}.employmentType`}
                      value={employment.employmentType}
                      handlechange={(e) => handleChange(e, index, "employment")}
                      options={[
                        { label: "None" },
                        { value: "TEMPORARY", label: "Temporary" },
                        { value: "CONTRACT", label: "Contract" },
                        { value: "FULLTIME", label: "Full-time" },
                        { value: "INTERNSHIP", label: "Internship" },
                        { value: "PARTIME", label: "Part-time" },
                      ]}
                    />
                    <InputField
                      label={"Start Date *"}
                      type="date"
                      name={`employmentDetails.${index}.startDate`}
                      value={employment.startDate}
                      handlechange={(e) => handleChange(e, index, "employment")}
                    />
                    <InputField
                      label={"End Date *"}
                      type="date"
                      name={`employmentDetails.${index}.endDate`}
                      value={employment.endDate}
                      handlechange={(e) => handleChange(e, index, "employment")}
                    />
                    <InputField
                      label={"Currently working"}
                      type="select"
                      name={`employmentDetails.${index}.currentlyEmployed`}
                      value={employment.currentlyEmployed ? "true" : "false"}
                      handlechange={(e) => handleChange(e, index, "employment")}
                      options={[
                        { value: "true", label: "Yes" },
                        { value: "false", label: "No" },
                      ]}
                    />
                  </Grid>
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      style={{ color: "red" }}
                      onClick={() => removeEmployment(index)}
                    >
                      <Delete />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="p-2 px-4 bg-blue-500 text-white rounded-md mt-6 flex items-center justify-center"
                onClick={addEmployment}
              >
                Add Employment
                <Add className="ml-2" />
              </button>
              <br></br>
            </Collapse>

          </div>
        )}
        {/* <div className="flex items-center justify-center min-h-screen"> */}
        <div className="flex justify-center my-6 space-x-4">
          <button
            type="submit"
            className="p-2 px-4 bg-black tracking-wide rounded-md hover:bg-transparent hover:text-black text-white border border-black"
            onClick={handleSubmit}
          >
            Update
          </button>
          <button
            type="button"
            className="p-2 rounded-md tracking-wide border border-black"
            onClick={() => navigate("/profile")}
          >
            Cancel
          </button>
        </div>
        {/* </div> */}

      </div>

      {/* Snackbar for Success/Warning/Error Messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SecurityBox />
    </>
  );
};

export default MyProfile;
